import { useState } from "react";
import type { View } from "../../../../../common/types.ts";
import { helpers } from "../../util/helpers.ts";
import { getCols } from "../../util/getCols.ts";
import { highlightLeaderText, MaybeBold, SeasonLabel } from "./common.tsx";
import i18n from "../../util/i18n.ts";
import { formatStatGameHigh } from "../PlayerStats.tsx";
import { SeasonIcons } from "../../components/SeasonIcons.tsx";
import HideableSection from "../../components/HideableSection.tsx";
import { DataTable } from "../../components/DataTable/index.tsx";
import { useRangeFooter } from "./useRangeFooter.ts";
import type { FooterRow } from "../../components/DataTable/Footer.tsx";
import { wrappedTeamAbbrevLink } from "../../components/TeamAbbrevLink.tsx";
import type { SuperCol } from "../../components/DataTable/index.tsx";
import PageTabs from "../../components/PageTabs.tsx";
import { MobileTooltip } from "../../components/MobileTooltip.tsx";

const hasStats = (
  careerStats: View<"player">["player"]["careerStats"],
  onlyShowIf: string[] | undefined,
) => {
  // For careerStatPlayoffs gp is undefined if there are no stats rows, ugh
  if (careerStats.gp === 0 || careerStats.gp === undefined) {
    return false;
  }

  if (onlyShowIf !== undefined) {
    for (const stat of onlyShowIf) {
      if (
        careerStats[stat]! > 0 ||
        (Array.isArray(careerStats[stat]) && (careerStats[stat] as any).length > 0)
      ) {
        return true;
      }
    }

    return false;
  }

  return true;
};

const SeasonRangeChoices = ({
  activeSeason,
  edge,
  onSelectSeason,
  seasons,
}: {
  activeSeason: number;
  edge: "end" | "start";
  onSelectSeason: (season: number) => void;
  seasons: number[];
}) => {
  const label =
    edge === "start"
      ? i18n.t("statsTable.rangeStart", {
          ns: "ui",
          defaultValue: "Start",
        })
      : i18n.t("statsTable.rangeEnd", {
          ns: "ui",
          defaultValue: "End",
        });

  return (
    <div className="player-season-range-choices" data-player-season-range-choices={edge}>
      <div className="small text-body-secondary">{label}</div>
      <div aria-label={label} className="d-flex flex-wrap gap-1" role="group">
        {seasons.map((season) => (
          <button
            aria-pressed={season === activeSeason}
            className={`btn btn-sm ${
              season === activeSeason ? "btn-secondary" : "btn-outline-secondary"
            }`}
            data-player-season-range-choice={season}
            key={season}
            onClick={() => {
              onSelectSeason(season);
            }}
            type="button"
          >
            {season}
          </button>
        ))}
      </div>
    </div>
  );
};

export const StatsTable = ({
  name,
  onlyShowIf,
  p,
  stats,
  superCols,
  leaders,
}: {
  name: string;
  onlyShowIf?: string[];
  p: View<"player">["player"];
  stats: string[];
  superCols?: SuperCol[];
  leaders: View<"player">["leaders"];
}) => {
  const hasRegularSeasonStats = hasStats(p.careerStats, onlyShowIf);
  const hasPlayoffStats = hasStats(p.careerStatsPlayoffs, onlyShowIf);

  // Show playoffs by default if that's all we have
  const [playoffs, setPlayoffs] = useState<boolean | "combined">(!hasRegularSeasonStats);
  const [showFullTable, setShowFullTable] = useState(false);

  // If game sim means we switch from having no stats to having some stats, make sure we're showing what we have
  if (hasRegularSeasonStats && !hasPlayoffStats && playoffs === true) {
    setPlayoffs(false);
  }
  if (!hasRegularSeasonStats && hasPlayoffStats && playoffs === false) {
    setPlayoffs(true);
  }

  let playerStats = p.stats.filter((ps) => ps.playoffs === playoffs);

  const rangeFooter = useRangeFooter(p.pid, playerStats);

  if (!hasRegularSeasonStats && !hasPlayoffStats) {
    return null;
  }

  const careerStats =
    playoffs === "combined"
      ? p.careerStatsCombined
      : playoffs
        ? p.careerStatsPlayoffs
        : p.careerStats;

  const rangeFooterState = rangeFooter.state;
  const rangeStatsPlayer =
    rangeFooterState.type === "open" ||
    rangeFooterState.type === "loading" ||
    rangeFooterState.type === "error"
      ? rangeFooterState.p
      : undefined;
  const rangeStats = rangeStatsPlayer
    ? playoffs === "combined"
      ? rangeStatsPlayer.careerStatsCombined
      : playoffs
        ? rangeStatsPlayer.careerStatsPlayoffs
        : rangeStatsPlayer.careerStats
    : undefined;

  const cols = getCols([
    "Year",
    "Team",
    "Age",
    ...stats.map((stat) =>
      stat === "pos" ? "Pos" : `stat:${stat.endsWith("Max") ? stat.replace("Max", "") : stat}`,
    ),
  ]);

  if (superCols) {
    superCols = helpers.deepCopy(superCols);

    // No name
    if (superCols[0]) {
      superCols[0].colspan -= 1;
    }
  }

  if (name === "Shot Locations") {
    cols.at(-3)!.title = "M";
    cols.at(-2)!.title = "A";
    cols.at(-1)!.title = "%";
  }

  const footer: FooterRow[] = [
    {
      data: [
        i18n.t("playerProfile.career", { ns: "ui", defaultValue: "Career" }),
        null,
        null,
        ...stats.map((stat) => formatStatGameHigh(careerStats, stat)),
      ],
    },
  ];

  if (
    rangeFooterState.type === "open" ||
    rangeFooterState.type === "loading" ||
    rangeFooterState.type === "error"
  ) {
    const rangeStatsValues: any[] = ([0, 1] as const).map((i) => {
      return {
        classNames: "p-0",
        value: (
          <SeasonRangeChoices
            key={i}
            activeSeason={rangeFooterState.seasonRange[i]}
            edge={i === 0 ? "start" : "end"}
            onSelectSeason={(value) => {
              const newSeasonRange: [number, number] = [...rangeFooterState.seasonRange];
              newSeasonRange[i] = value;

              if (i === 0 && newSeasonRange[1] < newSeasonRange[0]) {
                newSeasonRange[1] = newSeasonRange[0];
              } else if (i === 1 && newSeasonRange[1] < newSeasonRange[0]) {
                newSeasonRange[0] = newSeasonRange[1];
              }

              rangeFooter.setSeasonRange(newSeasonRange);
            }}
            seasons={rangeFooterState.seasons}
          />
        ),
      };
    });
    rangeStatsValues.push(
      rangeFooterState.type === "error" ? (
        <MobileTooltip
          title={i18n.t("common.error", {
            ns: "ui",
            defaultValue: "Error",
          })}
        >
          <span className="fw-semibold text-danger">!</span>
        </MobileTooltip>
      ) : null,
    );
    if (rangeStats) {
      rangeStatsValues.push(...stats.map((stat) => formatStatGameHigh(rangeStats, stat)));
    }

    footer.push({
      classNames: rangeFooterState.type === "loading" ? "text-body-secondary" : undefined,
      data: rangeStatsValues,
    });
  }

  const leadersType =
    playoffs === "combined" ? "combined" : playoffs === true ? "playoffs" : "regularSeason";

  let hasLeader = false;
  if (leadersType) {
    LEADERS_LOOP: for (const row of Object.values(leaders)) {
      if (row?.attrs.has("age")) {
        hasLeader = true;
        break;
      }

      for (const stat of stats) {
        if (row?.[leadersType].has(stat)) {
          hasLeader = true;
          break LEADERS_LOOP;
        }
      }
    }
  }

  const rows = [];

  let prevSeason;
  for (const [i, ps] of playerStats.entries()) {
    // Add blank rows for gap years if necessary
    if (prevSeason !== undefined && prevSeason < ps.season - 1) {
      const gapSeason = prevSeason + 1;

      rows.push({
        key: `gap-${gapSeason}`,
        data: [
          {
            searchValue: gapSeason,

            // i is used to index other sorts, so we need to fit in between
            sortValue: i - 0.5,

            value: null,
          },
          null,
          null,
          ...stats.map(() => null),
        ],
        classNames: "table-secondary",
      });
    }

    prevSeason = ps.season;

    const className = ps.hasTot ? "text-body-secondary" : undefined;

    rows.push({
      key: i,
      data: [
        {
          searchValue: ps.season,
          sortValue: i,
          value: (
            <>
              <SeasonLabel className={className} season={ps.season} />{" "}
              <SeasonIcons season={ps.season} awards={p.awards} playoffs={playoffs === true} />
            </>
          ),
        },
        wrappedTeamAbbrevLink({
          abbrev: ps.abbrev,
          className,
          season: ps.season,
          tid: ps.tid,
        }),
        <MaybeBold bold={leaders[ps.season]?.attrs.has("age")}>{ps.age}</MaybeBold>,
        ...stats.map((stat) => (
          <MaybeBold bold={!ps.hasTot && leaders[ps.season]?.[leadersType].has(stat)}>
            {formatStatGameHigh(ps, stat)}
          </MaybeBold>
        )),
      ],
      classNames: className,
    });
  }

  const showSelectSeasonRangeButton = rangeFooter.state.type === "closed";
  const showSeasonRangeChoices =
    rangeFooterState.type === "open" ||
    rangeFooterState.type === "loading" ||
    rangeFooterState.type === "error";
  const coreStats = stats.filter((stat) => stat !== "pos").slice(0, 5);
  const latestStats = playerStats.at(-1);

  return (
    <HideableSection title={name} description={hasLeader ? highlightLeaderText : null}>
      <div
        data-player-stats-mode={leadersType}
        data-player-stats-season-count={new Set(playerStats.map((row) => row.season)).size}
        data-player-stats-table={name}
        data-player-stats-range-state={rangeFooterState.type}
        {...(rangeFooterState.type === "open" ||
        rangeFooterState.type === "loading" ||
        rangeFooterState.type === "error"
          ? {
              "data-player-stats-range-start": rangeFooterState.seasonRange[0],
              "data-player-stats-range-end": rangeFooterState.seasonRange[1],
            }
          : {})}
      >
        <PageTabs
          ariaLabel={name}
          className="player-stats-page-tabs mb-2"
          tabs={[
            ...(hasRegularSeasonStats
              ? [
                  {
                    active: playoffs === false,
                    key: "regularSeason",
                    label: i18n.t("common.regularSeason", {
                      ns: "ui",
                      defaultValue: "Regular season",
                    }),
                    onClick: () => {
                      setPlayoffs(false);
                    },
                  },
                ]
              : []),
            ...(hasPlayoffStats
              ? [
                  {
                    active: playoffs === true,
                    key: "playoffs",
                    label: i18n.t("common.playoffs", {
                      ns: "ui",
                      defaultValue: "Playoffs",
                    }),
                    onClick: () => {
                      setPlayoffs(true);
                    },
                  },
                ]
              : []),
            ...(hasRegularSeasonStats && hasPlayoffStats
              ? [
                  {
                    active: playoffs === "combined",
                    key: "combined",
                    label: i18n.t("common.combined", {
                      ns: "ui",
                      defaultValue: "Combined",
                    }),
                    onClick: () => {
                      setPlayoffs("combined");
                    },
                  },
                ]
              : []),
          ]}
        />
        <table className="table table-sm table-borderless mb-3">
          <tbody>
            {latestStats ? (
              <tr>
                <th scope="row">
                  {i18n.t("statsTable.latestSeason", {
                    ns: "ui",
                    defaultValue: "Latest season",
                  })}
                </th>
                {coreStats.map((stat) => (
                  <td className="text-end" key={stat}>
                    <div className="small text-body-secondary">{stat}</div>
                    <div>{formatStatGameHigh(latestStats, stat)}</div>
                  </td>
                ))}
              </tr>
            ) : null}
            <tr>
              <th scope="row">
                {i18n.t("playerProfile.career", {
                  ns: "ui",
                  defaultValue: "Career",
                })}
              </th>
              {coreStats.map((stat) => (
                <td className="text-end" key={stat}>
                  <div className="small text-body-secondary">{stat}</div>
                  <div>{formatStatGameHigh(careerStats, stat)}</div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
        <details
          className="mb-2"
          data-native-details-host="player-stats-full-table"
          data-player-advanced-data-exception=""
          data-player-advanced-full-table-disclosure=""
          onToggle={(event) => {
            setShowFullTable(event.currentTarget.open);
          }}
          open={showFullTable}
        >
          <summary className="btn btn-sm btn-secondary">
            {i18n.t("statsTable.fullSeasonTable", {
              ns: "ui",
              defaultValue: "Full season table",
            })}
          </summary>
          {showFullTable ? (
            <div
              className="mt-2"
              data-player-advanced-full-table=""
              data-player-full-season-table=""
            >
              <DataTable
                classNameWrapper={showSelectSeasonRangeButton ? "mb-2" : "mb-3"}
                cols={cols}
                defaultSort={[0, "asc"]}
                defaultStickyCols={2}
                footer={footer}
                hideAllControls
                name={`Player:${name}`}
                rows={rows}
                superCols={superCols}
              />
              {showSeasonRangeChoices ? (
                <div className="d-md-none d-flex flex-column gap-2 mb-3">
                  {([0, 1] as const).map((i) => (
                    <SeasonRangeChoices
                      activeSeason={rangeFooterState.seasonRange[i]}
                      edge={i === 0 ? "start" : "end"}
                      key={i}
                      onSelectSeason={(value) => {
                        const newSeasonRange: [number, number] = [
                          ...rangeFooterState.seasonRange,
                        ];
                        newSeasonRange[i] = value;

                        if (i === 0 && newSeasonRange[1] < newSeasonRange[0]) {
                          newSeasonRange[1] = newSeasonRange[0];
                        } else if (i === 1 && newSeasonRange[1] < newSeasonRange[0]) {
                          newSeasonRange[0] = newSeasonRange[1];
                        }

                        rangeFooter.setSeasonRange(newSeasonRange);
                      }}
                      seasons={rangeFooterState.seasons}
                    />
                  ))}
                  {rangeFooterState.type === "open" && rangeStats ? (
                    <div
                      className="border rounded p-2"
                      data-player-season-range-readback=""
                      data-player-season-range-readback-end={rangeFooterState.seasonRange[1]}
                      data-player-season-range-readback-start={rangeFooterState.seasonRange[0]}
                      data-player-season-range-readback-stat-count={coreStats.length}
                    >
                      <div className="small text-body-secondary">
                        {rangeFooterState.seasonRange[0]}–{rangeFooterState.seasonRange[1]}
                      </div>
                      <div className="d-flex flex-wrap gap-3">
                        {coreStats.map((stat) => (
                          <div data-player-season-range-readback-stat={stat} key={stat}>
                            <span className="small text-body-secondary me-1">{stat}</span>
                            <span>{formatStatGameHigh(rangeStats, stat)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </details>
        {showFullTable && showSelectSeasonRangeButton ? (
          <button
            className="btn btn-sm btn-secondary mb-3"
            data-player-select-season-range=""
            onClick={rangeFooter.onOpen}
          >
            {i18n.t("statsTable.selectSeasonRange", {
              ns: "ui",
              defaultValue: "Select season range",
            })}
          </button>
        ) : null}
      </div>
    </HideableSection>
  );
};
